import type React from "react";

interface AnisoconiaToolProps {
	mode: "whole" | "region";
	headScale: number;
	scale: number;
	bridge: number;
	hasRegion: boolean;
	onChange: (patch: {
		mode?: "whole" | "region";
		headScale?: number;
		scale?: number;
		bridge?: number;
	}) => void;
	onClearRegion: () => void;
}

// Shared control sizing — geriatric touch targets (≥44px).
const TAB = "flex-1 min-h-[44px] rounded font-medium";
const PRESET = "min-h-[44px] px-3 rounded";

export const AnisoconiaTool: React.FC<AnisoconiaToolProps> = ({
	mode,
	headScale,
	scale,
	bridge,
	hasRegion,
	onChange,
	onClearRegion,
}) => {
	const sizePresets = [0.5, 0.75, 1, 1.5, 2];
	return (
		<div className="p-4 space-y-4">
			<div className="font-semibold">Anisoconia (Size)</div>

			{/* Whole head | Region */}
			<div className="flex gap-2">
				<button
					type="button"
					className={`${TAB} ${mode !== "region" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
					onClick={() => onChange({ mode: "whole" })}
				>
					Whole head
				</button>
				<button
					type="button"
					className={`${TAB} ${mode === "region" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
					onClick={() => onChange({ mode: "region" })}
				>
					Region
				</button>
			</div>

			{mode === "region" ? (
				<>
					<p className="text-sm text-gray-600">
						Tap the face to place the region, then drag to move, drag the corner
						to resize.
					</p>

					<div>
						<div className="text-sm font-medium mb-1">
							Region scale: {Math.round(scale * 100)}%
						</div>
						<div className="flex gap-2 mb-2 flex-wrap">
							{sizePresets.map((p) => (
								<button
									key={p}
									type="button"
									className={`${PRESET} ${scale === p ? "bg-blue-500 text-white" : "bg-gray-200"}`}
									onClick={() => onChange({ scale: p })}
									disabled={!hasRegion}
								>
									{Math.round(p * 100)}%
								</button>
							))}
						</div>
						<input
							type="range"
							min={0.5}
							max={2}
							step={0.01}
							value={scale}
							disabled={!hasRegion}
							onChange={(e) =>
								onChange({ scale: Number.parseFloat(e.target.value) })
							}
							aria-label="Region scale"
							className="w-full"
						/>
					</div>

					<div>
						<div className="text-sm font-medium mb-1">
							Bridge (edge softness): {Math.round(bridge * 100)}%
						</div>
						<input
							type="range"
							min={0}
							max={1}
							step={0.01}
							value={bridge}
							disabled={!hasRegion}
							onChange={(e) =>
								onChange({ bridge: Number.parseFloat(e.target.value) })
							}
							aria-label="Bridge edge softness"
							className="w-full"
						/>
					</div>

					{hasRegion && (
						<button
							type="button"
							className={`${PRESET} bg-gray-200 w-full`}
							onClick={onClearRegion}
						>
							Clear region
						</button>
					)}
				</>
			) : (
				<div>
					<div className="text-sm font-medium mb-1">
						Head size: {Math.round(headScale * 100)}%
					</div>
					<div className="flex gap-2 mb-2 flex-wrap">
						{sizePresets.map((p) => (
							<button
								key={p}
								type="button"
								className={`${PRESET} ${headScale === p ? "bg-blue-500 text-white" : "bg-gray-200"}`}
								onClick={() => onChange({ headScale: p })}
							>
								{Math.round(p * 100)}%
							</button>
						))}
					</div>
					<input
						type="range"
						min={0.5}
						max={2}
						step={0.01}
						value={headScale}
						onChange={(e) =>
							onChange({ headScale: Number.parseFloat(e.target.value) })
						}
						aria-label="Head scale"
						className="w-full"
					/>
				</div>
			)}
		</div>
	);
};
